/**
 * Code map — a short, navigable map of a large stored procedure or Java
 * method, then the real lines of one node at a time. Built by Compass from
 * code (never by a model). The panel keeps what makes a map honest: node
 * counts (missing / complete), folded runs, both completeness counts and
 * every warning — a capped or incomplete map must never look complete.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  MapCondition,
  MapCounts,
  MapNode,
  MapResponse,
  NodeResponse,
  mapMethod,
  openProcNode,
  readProc,
} from '../../services/compassApi';

interface Props {
  kind: 'procedure' | 'method';
  itemKey: string;
}

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

function compact(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

function Callout({ text, tone }: { text: string; tone: 'amber' | 'red' | 'gray' }) {
  const cls = {
    amber: 'bg-amber-50 border-amber-300 text-amber-900',
    red: 'bg-red-50 border-red-300 text-red-800',
    gray: 'bg-gray-50 border-gray-300 text-gray-700',
  }[tone];
  return (
    <div className={`mt-1 border rounded px-2 py-1 text-[11px] flex gap-1 ${cls}`}>
      <AlertTriangle size={12} className="shrink-0 mt-0.5" />
      <span className="whitespace-pre-wrap">{text}</span>
    </div>
  );
}

function CountsLine({ counts, what }: { counts?: MapCounts; what: string }) {
  if (!counts) return null;
  const missing = counts.missing ?? 0;
  const incomplete = counts.complete === false || missing > 0;
  return (
    <div className={`text-[11px] mt-1 ${incomplete ? 'text-amber-900 font-medium' : 'text-gray-600'}`}>
      {what}: showing {counts.returned ?? '?'} of {counts.total ?? '?'}
      {incomplete
        ? ` — ${missing} not shown, so this is NOT complete`
        : counts.complete === true
          ? ' — complete'
          : ''}
    </div>
  );
}

function conditionOf(n: MapNode): MapCondition | null {
  if (!n.condition) return null;
  return typeof n.condition === 'string' ? { text: n.condition } : n.condition;
}

function NodeLines({ data }: { data: NodeResponse }) {
  return (
    <div className="mt-1 border border-gray-200 rounded bg-white">
      {data.lines && data.lines.length > 0 ? (
        <pre className="text-[11px] leading-4 overflow-x-auto max-h-72 overflow-y-auto p-1">
          {data.lines.map((l) => (
            <div key={l.line_number} className={l.commented ? 'text-gray-400 italic' : ''}>
              <span className="select-none text-gray-400 mr-2 inline-block w-12 text-right">{l.line_number}</span>
              {l.content}
              {l.commented ? '   -- commented out' : ''}
            </div>
          ))}
        </pre>
      ) : (
        <p className="text-xs text-gray-600 p-1">No lines returned.</p>
      )}
      <div className="px-1 pb-1">
        <CountsLine counts={data.counts?.lines} what="Lines" />
        {data.comment_warning && <Callout tone="amber" text={data.comment_warning} />}
        {data.note && <Callout tone="gray" text={data.note} />}
        {data.warning && <Callout tone="amber" text={data.warning} />}
      </div>
    </div>
  );
}

function NodeRow({ node, itemKey, canOpen }: { node: MapNode; itemKey: string; canOpen: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<NodeResponse | null>(null);
  const cond = conditionOf(node);

  const open = async (fromLine?: number) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await openProcNode(itemKey, node.id, { maxChars: 4000, fromLine });
      setData((prev) =>
        fromLine && prev
          ? { ...r, lines: [...(prev.lines ?? []), ...(r.lines ?? [])] }
          : r,
      );
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-gray-100 py-1">
      <div className="flex items-start gap-2 text-xs">
        <span className="font-mono text-emerald-800 w-14 shrink-0">{node.id}</span>
        <span className="w-24 shrink-0 text-gray-700">
          {node.kind ?? '—'}
          {node.touches && node.touches.length > 0 && (
            <span title={`touches: ${node.touches.join(', ')}`} className="ml-1 text-emerald-700 font-bold">*</span>
          )}
        </span>
        <span className="w-24 shrink-0 font-mono text-gray-500">
          {node.start_line != null ? `${node.start_line}${node.end_line != null && node.end_line !== node.start_line ? `–${node.end_line}` : ''}` : ''}
        </span>
        <span className="flex-1 min-w-0 font-mono break-all text-gray-900">{node.head ?? ''}</span>
        {canOpen && (
          <button
            onClick={() => (data ? setData(null) : open())}
            disabled={busy}
            className="shrink-0 text-[11px] px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
          >
            {busy ? '…' : data ? 'Hide' : 'Open lines'}
          </button>
        )}
      </div>
      {cond && cond.text && (
        <div className="ml-16 text-[11px] text-gray-600">
          {/* "Guarded by" only when Compass proved it; anything else is a guess to confirm. */}
          {cond.why === 'control_dependence_exact' ? 'Guarded by' : 'Nearest condition above, open to confirm'}
          {cond.line != null ? ` (line ${cond.line})` : ''}:{' '}
          <span className="font-mono">{cond.text}</span>
        </div>
      )}
      {!cond && node.why && (
        <div className="ml-16 text-[11px] text-gray-500">
          Shown as the nearest condition above a match, open to confirm
        </div>
      )}
      {node.folded_lines != null && node.folded_lines > 0 && (
        <div className="ml-16 text-[11px] text-gray-500">{node.folded_lines} plain line(s) folded</div>
      )}
      {err && <p className="ml-16 text-[11px] text-red-700">{err}</p>}
      {data && (
        <div className="ml-16">
          <NodeLines data={data} />
          {data.next_from_line != null && (
            <button
              onClick={() => open(data.next_from_line ?? undefined)}
              disabled={busy}
              className="mt-1 text-[11px] px-2 py-0.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Next page (from line {data.next_from_line})
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function CodeMapPanel({ kind, itemKey }: Props) {
  const [touchText, setTouchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<MapResponse | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const touch = touchText.split(',').map((t) => t.trim()).filter(Boolean);
    try {
      setData(kind === 'procedure' ? await readProc(itemKey, { touch }) : await mapMethod(itemKey, { touch }));
    } catch (e) {
      setErr(errText(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [itemKey, kind]); // touchText is read when the button is pressed, not on every keystroke

  useEffect(() => {
    load();
  }, [load]);

  const tier = data?.tier ?? data?.size_tier;
  const wholeBody = tier === 'whole_body';
  const notMapped = data?.mapped === false;
  const nodes = data?.nodes ?? [];
  const folded = data?.folded && typeof data.folded === 'object' ? (data.folded as { nodes?: number; lines?: number; note?: string }) : null;
  const touchShown = Array.isArray(data?.touch) ? data?.touch.join(', ') : data?.touch;

  return (
    <div className="mt-2 border-t border-emerald-100 pt-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <input
          value={touchText}
          onChange={(e) => setTouchText(e.target.value)}
          placeholder="Filter to tables or columns (comma separated, optional)"
          className="flex-1 min-w-[16rem] border border-gray-300 rounded px-2 py-1"
        />
        <button onClick={load} disabled={loading} className="px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
          {loading ? 'Loading…' : 'Reload map'}
        </button>
      </div>

      {loading && (
        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading map…</p>
      )}
      {err && <Callout tone="red" text={err} />}

      {data && (
        <div className="mt-1">
          {notMapped ? (
            <p className="text-xs text-gray-700">
              Compass did not map this {kind}{data.reason ? `: ${data.reason}` : ''}.
              {data.read_instead === 'get_method_source' || kind === 'method' ? ' Use “View source” to read it.' : ' Use “View source” to read the body.'}
            </p>
          ) : wholeBody ? (
            <p className="text-xs text-gray-700">
              Small {kind}: Compass returned the whole body{(data.body_chars ?? data.total_chars) != null ? ` (${(data.body_chars ?? data.total_chars)!.toLocaleString()} characters)` : ''}, so there is no map.
            </p>
          ) : (
            <p className="text-xs text-gray-700">
              Map of this {kind}. A map is a guide to where to look: open a node to read its real lines.
            </p>
          )}

          {wholeBody && data.content && (
            <pre className="text-[11px] leading-4 mt-1 max-h-72 overflow-auto border border-gray-200 rounded bg-white p-1">
              {data.content.split('\n').map((l, i) => (
                <div key={i}><span className="select-none text-gray-400 mr-2 inline-block w-12 text-right">{i + 1}</span>{l}</div>
              ))}
            </pre>
          )}

          {!notMapped && (data.total_lines != null || data.total_chars != null) && (
            <div className="text-[11px] text-gray-600 mt-1">
              {data.total_lines != null ? `${data.total_lines.toLocaleString()} lines` : ''}
              {data.total_chars != null ? `, ${data.total_chars.toLocaleString()} characters` : ''}
              {data.filtered_by_touch && touchShown ? ` — filtered to: ${touchShown}` : ''}
            </div>
          )}
          <CountsLine counts={data.counts?.nodes} what="Nodes" />
          {folded ? (
            <div className="text-[11px] text-gray-600 mt-1">
              Folded: {folded.nodes ?? '?'} plain node(s), {folded.lines ?? '?'} line(s){folded.note ? ` — ${folded.note}` : ''}
            </div>
          ) : (
            data.folded != null && <div className="text-[11px] text-gray-600 mt-1">Folded: {compact(data.folded)}</div>
          )}
          {data.completeness && Object.keys(data.completeness).length > 0 && (
            <div className="text-[11px] text-gray-600 mt-1">
              Completeness (map vs source counts):
              <ul className="ml-4 list-disc">
                {Object.entries(data.completeness).map(([k, v]) => (
                  <li key={k}><span className="font-medium">{k}</span>: <span className="font-mono">{compact(v)}</span></li>
                ))}
              </ul>
            </div>
          )}
          {data.body_unavailable && <Callout tone="amber" text={`Whole body unavailable: ${data.body_unavailable}`} />}
          {data.note && <Callout tone="gray" text={data.note} />}
          {data.warning && <Callout tone="amber" text={data.warning} />}

          {!wholeBody && nodes.length > 0 && (
            <div className="mt-2">
              <div className="flex gap-2 text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-200 pb-0.5">
                <span className="w-14">Node</span><span className="w-24">Kind</span><span className="w-24">Lines</span><span>What</span>
              </div>
              {nodes.map((n) => (
                <NodeRow key={n.id} node={n} itemKey={itemKey} canOpen={kind === 'procedure'} />
              ))}
            </div>
          )}
          {!wholeBody && nodes.length === 0 && !loading && (
            <p className="text-xs text-gray-600 mt-1">No nodes returned. See the notes above, or read the source.</p>
          )}
          {kind === 'method' && !wholeBody && (
            <p className="text-[11px] text-gray-500 mt-1">To read a method's lines, use “View source”.</p>
          )}

          <button onClick={() => setShowRaw((v) => !v)} className="mt-2 text-[11px] text-gray-500 underline">
            {showRaw ? 'Hide' : 'Show'} raw Compass response
          </button>
          {showRaw && <pre className="text-[10px] max-h-60 overflow-auto bg-gray-50 border border-gray-200 rounded p-1 mt-1">{JSON.stringify(data, null, 2)}</pre>}
        </div>
      )}
    </div>
  );
}